'use client';

import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';

const HIDDEN_TRANSFORM = {
  up: 'translate-y-16',
  left: '-translate-x-16',
  right: 'translate-x-16',
} as const;

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  /** Stagger successive cards slightly so a row doesn't all snap in at once. */
  delayMs?: number;
  /** Rendered tag — e.g. "li" when the reveal wraps a list item, so the parent <ol>/<ul>
   *  still has only <li> as direct children. Defaults to "div". */
  as?: ElementType;
  /** Direction the element slides in from. Defaults to "up". */
  from?: keyof typeof HIDDEN_TRANSFORM;
}

// Generic fade+slide reveal for anything that should animate in as it scrolls into view.
// A slightly more pronounced motion makes the founder page feel active without
// creating a jarring or overly aggressive effect.
export default function ScrollReveal({
  children,
  className = '',
  delayMs = 0,
  as: Tag = 'div',
  from = 'up',
}: ScrollRevealProps) {
  const [visible, setVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setReduceMotion(true);
      setVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={reduceMotion ? undefined : { transitionDelay: `${delayMs}ms` }}
      className={`${reduceMotion ? '' : 'transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform'} ${
        visible ? 'translate-x-0 translate-y-0 opacity-100' : `${HIDDEN_TRANSFORM[from]} opacity-0`
      } ${className}`}
    >
      {children}
    </Tag>
  );
}
